/**
 * RBAC - Modulo 7 (RF07.2).
 *
 * Fonte unica de verdade sobre "quem pode o que", consumida tanto pelo Fastify
 * (para negar a requisicao) quanto pelo React (para esconder o que o usuario
 * nao pode usar). Duplicar essa matriz nos dois lados garantiria divergencia.
 */

export const ROLES = [
  'ADMIN',
  'SECRETARIA',
  'TESOURARIA',
  'PASTORAL_DIZIMO',
  'COORDENADOR_EVENTOS',
  'CATEQUISTA',
] as const

export type Role = (typeof ROLES)[number]

export const ROLE_LABELS: Record<Role, string> = {
  ADMIN: 'Administrador Geral',
  SECRETARIA: 'Secretaria Paroquial',
  TESOURARIA: 'Tesouraria / Financeiro',
  PASTORAL_DIZIMO: 'Pastoral do Dizimo',
  COORDENADOR_EVENTOS: 'Coordenador de Eventos',
  CATEQUISTA: 'Catequista',
}

export const ROLE_DESCRIPTIONS: Record<Role, string> = {
  ADMIN: 'Acesso irrestrito a todos os modulos, configuracoes e usuarios.',
  SECRETARIA:
    'Portal de noticias, horarios de missa, agenda e inscricoes de eventos. Sem acesso ao financeiro ou aos dizimos.',
  TESOURARIA:
    'Gestao financeira, balancetes de eventos e relatorios de dizimo. Sem acesso a dados pedagogicos da catequese.',
  PASTORAL_DIZIMO:
    'Exclusivo para cadastro de dizimistas e lancamento de dizimos. Sem acesso aos demais dados financeiros.',
  COORDENADOR_EVENTOS:
    'Estoque, PDV, patrocinadores e inscritos apenas dos eventos em que foi designado.',
  CATEQUISTA: 'Apenas as proprias turmas e a chamada de frequencia da catequese.',
}

/**
 * Permissoes no formato `recurso:acao`.
 *
 * Os modulos 2, 4, 5 e 6 ja aparecem aqui mesmo fora do escopo desta entrega:
 * declarar as permissoes agora mantem a matriz coerente com o RF07.2 e evita
 * renomear papeis quando esses modulos chegarem.
 */
export const PERMISSIONS = [
  // Modulo 1 - portal e noticias
  'content:read',
  'content:write',
  'content:publish',
  'content:delete',
  'taxonomy:manage',
  'notice:manage',
  'mass_schedule:manage',
  'media:upload',

  // Modulo 3 - eventos
  'event:read',
  'event:write',
  'event:delete',
  'registration:read',
  'registration:write',
  'registration:export',
  'event_finance:read',
  'event_finance:write',
  'sponsor:manage',
  'inventory:read',
  'inventory:write',
  'pos:operate',

  // Modulos futuros
  'finance:read',
  'finance:write',
  'dizimo:read',
  'dizimo:write',
  'catequese:read',
  'catequese:write',
  'agenda:read',
  'agenda:write',

  // Administracao
  'user:manage',
  'audit:read',
  'settings:manage',
] as const

export type Permission = (typeof PERMISSIONS)[number]

const SECRETARIA_PERMISSIONS: Permission[] = [
  'content:read',
  'content:write',
  'content:publish',
  'content:delete',
  'taxonomy:manage',
  'notice:manage',
  'mass_schedule:manage',
  'media:upload',
  'event:read',
  'event:write',
  'registration:read',
  'registration:write',
  'registration:export',
  'sponsor:manage',
  'agenda:read',
  'agenda:write',
  'catequese:read',
  'catequese:write',
]

const TESOURARIA_PERMISSIONS: Permission[] = [
  'content:read',
  'event:read',
  'event_finance:read',
  'event_finance:write',
  'inventory:read',
  'finance:read',
  'finance:write',
  // RF07.2: a tesouraria le relatorios de dizimo, mas nao lanca contribuicoes
  // (isso e da Pastoral do Dizimo).
  'dizimo:read',
  'media:upload',
]

const COORDENADOR_PERMISSIONS: Permission[] = [
  'content:read',
  'event:read',
  'event:write',
  'registration:read',
  'registration:write',
  'registration:export',
  'event_finance:read',
  'event_finance:write',
  'sponsor:manage',
  'inventory:read',
  'inventory:write',
  'pos:operate',
  'media:upload',
]

export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  ADMIN: PERMISSIONS,
  SECRETARIA: SECRETARIA_PERMISSIONS,
  TESOURARIA: TESOURARIA_PERMISSIONS,
  PASTORAL_DIZIMO: ['dizimo:read', 'dizimo:write'],
  COORDENADOR_EVENTOS: COORDENADOR_PERMISSIONS,
  CATEQUISTA: ['catequese:read'],
}

/**
 * Permissoes cujo alcance depende de QUAL evento esta sendo acessado.
 * Para papeis sujeitos a escopo, so valem nos eventos em que o usuario e
 * membro (tabela EventMember).
 */
export const EVENT_SCOPED_PERMISSIONS: readonly Permission[] = [
  'event:write',
  'event:delete',
  'registration:read',
  'registration:write',
  'registration:export',
  'event_finance:read',
  'event_finance:write',
  'sponsor:manage',
  'inventory:read',
  'inventory:write',
  'pos:operate',
]

/**
 * Papeis com alcance paroquial: ja respondem por toda a paroquia, portanto nao
 * passam pela verificacao de membro do evento. O Coordenador de Eventos fica
 * deliberadamente fora - e exatamente a restricao que o RF07.2 pede.
 */
export const PARISH_WIDE_ROLES: readonly Role[] = ['ADMIN', 'SECRETARIA', 'TESOURARIA']

export function isRole(value: unknown): value is Role {
  return typeof value === 'string' && (ROLES as readonly string[]).includes(value)
}

/** O papel possui a permissao, ignorando escopo por evento. */
export function can(role: Role, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role].includes(permission)
}

export function canAny(role: Role, permissions: readonly Permission[]): boolean {
  return permissions.some((p) => can(role, p))
}

/**
 * A permissao precisa ser confirmada contra a lista de eventos do usuario.
 * Quando true, o chamador tem de checar EventMember antes de liberar.
 */
export function needsEventScope(role: Role, permission: Permission): boolean {
  if (PARISH_WIDE_ROLES.includes(role)) return false
  return EVENT_SCOPED_PERMISSIONS.includes(permission)
}

/** Permissoes efetivas de um papel - enviadas ao front em `GET /api/auth/me`. */
export function permissionsFor(role: Role): Permission[] {
  return [...ROLE_PERMISSIONS[role]]
}
